<script setup lang="ts">
  import { IconAgencyStructure } from '@gat-ship/icons'
  import { type CreateNewAccessRoleRequestParams } from '@gat-ship/types'
  import { testIds } from '@gat-ship/types'
  import { useCommandId } from '@gat-ship/utils'
  import { globalUtils } from '@gat-ship/utils'
  import {
    Button,
    FormField,
    FormSubmit,
    InputGroupInput,
    InputGroupTextarea,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderIcon,
    ModalHeaderTitle,
    ModalSection,
    useAppLoading,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import { FieldGroup } from '@gat-ship/vue-components'
  import { useAccessControlApiCalls } from '../../composables/useAccessControlApiCalls'
  import {
    useAccessRolesCollectionProvider,
    type ShowAddAccessRoleDialogParams
  } from '../providers/access-roles-collection-context'

  const isOpen = defineModel<boolean>('open', { default: false })
  const accessRolesProvider = useAccessRolesCollectionProvider()
  const { createNewAccessRoleRequest } = useAccessControlApiCalls()
  const { commandId, newCommandId } = useCommandId()
  const { setLoadingId, removeLoadingId } = useAppLoading()

  const accessRoleSchema = z.object({
    roleId: z.string().min(1),
    name: z.string().min(1, 'Name is required').max(120, 'Max 120 characters'),
    description: z
      .string()
      .min(1, 'Description is required')
      .max(500, 'Max 500 characters')
  })
  type AccessRoleFormValues = z.infer<typeof accessRoleSchema>

  const form = useForm({
    defaultValues: {
      roleId: '',
      name: '',
      description: ''
    } as AccessRoleFormValues,
    validators: {
      onChange: accessRoleSchema
    },
    onSubmit: async ({ value }) => {
      const payload: CreateNewAccessRoleRequestParams = {
        commandId: commandId.value,
        payload: {
          roleId: value.roleId,
          name: value.name,
          description: value.description
        }
      }
      setLoadingId(['add-role-btn', payload.payload.roleId])
      accessRolesProvider.dataSource.addAccessRole({
        id: payload.payload.roleId,
        name: payload.payload.name,
        description: payload.payload.description
      })
      try {
        await createNewAccessRoleRequest(payload)
        isOpen.value = false
      } catch (error) {
        accessRolesProvider.dataSource.removeAccessRole(payload.payload.roleId)
        throw error
      } finally {
        removeLoadingId(['add-role-btn', payload.payload.roleId])
      }
    }
  })

  function showModal({ accessRole }: ShowAddAccessRoleDialogParams) {
    newCommandId()
    form.reset({
      roleId: accessRole?.id ?? globalUtils.functions.uuid(),
      name: accessRole?.name ?? '',
      description: accessRole?.description ?? ''
    })
    isOpen.value = true
  }

  defineExpose({ showModal })
</script>

<template>
  <modal v-model:open="isOpen">
    <ModalHeader>
      <ModalHeaderTitle>
        <ModalHeaderIcon>
          <IconAgencyStructure class="size-5" :stroke-width="1.5" />
        </ModalHeaderIcon>
        Add Role
      </ModalHeaderTitle>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <FieldGroup>
          <FormField :form="form" name="name" label="Name" required>
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                autofocus
                :data-testid="testIds.company.modal.nameInput"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
          <FormField
            :form="form"
            name="description"
            label="Description"
            required
          >
            <template #default="{ field }">
              <InputGroupTextarea
                :id="field.name"
                :model-value="field.state.value ?? ''"
                :data-testid="testIds.company.modal.descriptionInput"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <FormSubmit :form="form" label="Add role" :require-dirty="false" />
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </modal>
</template>
